import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import * as _ from "lodash-es";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { SettingsRowMeta, useSettingsPage } from "./SettingsPageContext";

export type EditedItem = {
    /** Supplier model / settings key. */
    key: string;
    /** Translation key of the row's label (falls back to the key). */
    titleKey: string;
    /** Plain text label that wins over titleKey (a workflow's name). */
    title?: string;
    /** DOM id of the row to jump to. */
    rowId: string;
    sectionTitleKey?: string;
    cardTitleKey?: string;
    /** Human-readable before/after values when they are short enough to show. */
    before?: string;
    after?: string;
};

type Props = {
    editedItems: EditedItem[];
    /** True while a save is in flight. */
    saving: boolean;
    onSave: () => void;
    onDiscard: () => void;
};

const MAX_SEARCH_RESULTS = 30;

/**
 * Closes a popover when the pointer goes down outside it or Escape is pressed.
 */
const useDismiss = (open: boolean, close: () => void, ref: React.RefObject<HTMLElement | null>) => {
    useEffect(() => {
        if (!open) {
            return;
        }

        const onPointerDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                close();
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                close();
            }
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open, close, ref]);
};

type MenuProps = {
    label: React.ReactNode;
    iconClass?: string;
    testId: string;
    children: (close: () => void) => React.ReactNode;
    menuClassName?: string;
};

/** A breadcrumb button that opens a jump menu beneath it. */
const JumpMenu = (props: MenuProps) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const close = useCallback(() => setOpen(false), []);

    useDismiss(open, close, ref);

    return (
        <div className={"SettingsHeader-menu"} ref={ref}>
            <button
                type={"button"}
                className={"SettingsHeader-crumb" + (open ? " open" : "")}
                aria-haspopup={"menu"}
                aria-expanded={open}
                data-testid={props.testId}
                onClick={() => setOpen(!open)}>
                {props.iconClass ? <i className={props.iconClass} aria-hidden={true} /> : null}
                <span className={"SettingsHeader-crumbLabel"}>{props.label}</span>
                <i className={"fas " + (open ? "fa-chevron-up" : "fa-chevron-down")} aria-hidden={true} />
            </button>
            {open ? (
                <div className={"SettingsMenu " + (props.menuClassName || "")} role={"menu"}>
                    {props.children(close)}
                </div>
            ) : null}
        </div>
    );
};

/**
 * The sticky bar at the top of the settings page: title, two scroll-following jump menus,
 * search over every row, and the one save state for the whole page.
 */
export const SettingsHeader = (props: Props) => {
    const intl = useIntl();
    const page = useSettingsPage();
    const barRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const unsavedRef = useRef<HTMLDivElement>(null);

    const [query, setQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [unsavedOpen, setUnsavedOpen] = useState(false);

    const closeSearch = useCallback(() => setSearchOpen(false), []);
    const closeUnsaved = useCallback(() => setUnsavedOpen(false), []);

    useDismiss(searchOpen, closeSearch, searchRef);
    useDismiss(unsavedOpen, closeUnsaved, unsavedRef);

    // Report the bar's height so jumps and scroll-spy can offset by it.
    const { setHeaderHeight } = page;

    useEffect(() => {
        const element = barRef.current;

        if (!element) {
            return;
        }

        const report = () => setHeaderHeight(element.offsetHeight);

        report();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(report);
        observer.observe(element);

        return () => observer.disconnect();
    }, [setHeaderHeight]);

    // ⌘K / Ctrl+K focuses search while the page is open.
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                inputRef.current?.focus();
                setSearchOpen(true);
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    const t = useCallback((id?: string) => (id ? intl.formatMessage({ id, defaultMessage: `_${id}_` }) : ""), [intl]);

    const activeSection = page.sections.find((section) => section.id === page.activeSectionId) || page.sections[0];
    const cardsInActiveSection = page.cards.filter((card) => card.sectionId === (activeSection ? activeSection.id : ""));
    const activeCard = cardsInActiveSection.find((card) => card.id === page.activeCardId) || cardsInActiveSection[0];

    /** Sections grouped under their group heading, groups in first-seen order. */
    const sectionGroups = useMemo(() => {
        const groups: { groupKey: string; sections: typeof page.sections }[] = [];

        for (const section of page.sections) {
            let group = groups.find((candidate) => candidate.groupKey === section.groupKey);

            if (!group) {
                group = { groupKey: section.groupKey, sections: [] };
                groups.push(group);
            }

            group.sections.push(section);
        }

        return groups;
    }, [page.sections]);

    /**
     * The page only knows which keys changed; the registry knows where those rows live. Fill in
     * the row id, label and breadcrumb from the registered row that edits the key.
     */
    const { rows, sections, cards } = page;

    // Work that saves on its own (an open workflow) joins the list, jumping to the row that hosts it.
    const pendingItems = useMemo<EditedItem[]>(
        () => page.pending.map((change) => ({ key: change.key, titleKey: change.key, title: change.title, rowId: change.rowId })),
        [page.pending]
    );

    const editedItems = useMemo(() => {
        return [...props.editedItems, ...pendingItems].map((item) => {
            const row = rows.find((candidate) => candidate.id === item.rowId || candidate.name === item.key);

            if (!row) {
                return item;
            }

            const section = sections.find((candidate) => candidate.id === row.sectionId);
            const card = cards.find((candidate) => candidate.id === row.cardId);

            return {
                ...item,
                rowId: row.id,
                titleKey: row.titleKey,
                sectionTitleKey: section ? section.titleKey : item.sectionTitleKey,
                cardTitleKey: card ? card.titleKey : item.cardTitleKey,
            };
        });
    }, [props.editedItems, pendingItems, rows, sections, cards]);

    const editedCountBySection = useMemo(() => {
        const counts: Record<string, number> = {};

        for (const item of editedItems) {
            const row = rows.find((candidate) => candidate.id === item.rowId);

            if (row) {
                counts[row.sectionId] = (counts[row.sectionId] || 0) + 1;
                counts[row.cardId] = (counts[row.cardId] || 0) + 1;
            }
        }

        return counts;
    }, [editedItems, rows]);

    /** Rows whose translated label or help text contains the query, grouped by section › card. */
    const results = useMemo(() => {
        const needle = query.trim().toLowerCase();

        if (!needle) {
            return [] as { row: SettingsRowMeta; sectionTitle: string; cardTitle: string; title: string; detail: string }[];
        }

        const matches: { row: SettingsRowMeta; sectionTitle: string; cardTitle: string; title: string; detail: string }[] = [];

        for (const row of rows) {
            const title = t(row.titleKey);
            const detail = t(row.detailKey);

            if (title.toLowerCase().includes(needle) || detail.toLowerCase().includes(needle) || row.name.includes(needle)) {
                const section = sections.find((candidate) => candidate.id === row.sectionId);
                const card = cards.find((candidate) => candidate.id === row.cardId);

                matches.push({ row, title, detail, sectionTitle: t(section?.titleKey), cardTitle: t(card?.titleKey) });

                if (matches.length >= MAX_SEARCH_RESULTS) {
                    break;
                }
            }
        }

        return matches;
    }, [query, rows, sections, cards, t]);

    useEffect(() => {
        setCursor(0);
    }, [query]);

    const pickResult = (index: number) => {
        const result = results[index];

        if (result) {
            page.jumpToRow(result.row.id);
            setSearchOpen(false);
        }
    };

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((current) => Math.min(current + 1, Math.max(results.length - 1, 0)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((current) => Math.max(current - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            pickResult(cursor);
        } else if (e.key === "Escape") {
            setSearchOpen(false);
            inputRef.current?.blur();
        }
    };

    const editedCount = editedItems.length;
    const hasChanges = editedCount > 0;

    // The model saves through the page; registered work saves itself. Save and Discard cover both.
    const saveAll = () => {
        if (props.editedItems.length) {
            props.onSave();
        }

        page.pending.forEach((change) => change.save());
    };

    const discardAll = () => {
        if (props.editedItems.length) {
            props.onDiscard();
        }

        page.pending.forEach((change) => change.discard());
    };

    let lastGroup = "";

    return (
        <div className={"SettingsHeader"} ref={barRef} data-testid={"settings-header"}>
            <div className={"SettingsHeader-title"}>
                <OwLabel title={"business_settings"} noBottomMargin={true} />
            </div>
            <span className={"SettingsHeader-divider"} />

            <JumpMenu
                testId={"section-menu"}
                iconClass={activeSection ? activeSection.iconClass : "fad fa-cog"}
                label={activeSection ? t(activeSection.titleKey) : ""}
                menuClassName={"sections"}>
                {(close) => (
                    <div className={"SettingsMenu-columns"}>
                        {sectionGroups.map((group) => (
                            <div key={group.groupKey} className={"SettingsMenu-group"}>
                                <div className={"SettingsMenu-eyebrow"}>{t(group.groupKey)}</div>
                                {group.sections.map((section) => (
                                    <button
                                        key={section.id}
                                        type={"button"}
                                        role={"menuitem"}
                                        className={
                                            "SettingsMenu-item" + (activeSection && activeSection.id === section.id ? " active" : "")
                                        }
                                        data-testid={`section-menu-${section.id}`}
                                        onClick={() => {
                                            page.jumpTo(section.id);
                                            close();
                                        }}>
                                        <i className={section.iconClass} aria-hidden={true} />
                                        <span>{t(section.titleKey)}</span>
                                        {editedCountBySection[section.id] ? (
                                            <span className={"SettingsMenu-count"}>
                                                {editedCountBySection[section.id]} {t("edited").toLowerCase()}
                                            </span>
                                        ) : null}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </JumpMenu>

            {cardsInActiveSection.length ? (
                <>
                    <i className={"fas fa-chevron-right SettingsHeader-crumbSeparator"} aria-hidden={true} />
                    <JumpMenu testId={"card-menu"} label={activeCard ? t(activeCard.titleKey) : ""}>
                        {(close) =>
                            cardsInActiveSection.map((card) => (
                                <button
                                    key={card.id}
                                    type={"button"}
                                    role={"menuitem"}
                                    className={"SettingsMenu-item" + (activeCard && activeCard.id === card.id ? " active" : "")}
                                    data-testid={`card-menu-${card.id}`}
                                    onClick={() => {
                                        page.jumpTo(card.id);
                                        close();
                                    }}>
                                    <span>{t(card.titleKey)}</span>
                                    {editedCountBySection[card.id] ? (
                                        <span className={"SettingsMenu-count"}>
                                            {editedCountBySection[card.id]} {t("edited").toLowerCase()}
                                        </span>
                                    ) : null}
                                </button>
                            ))
                        }
                    </JumpMenu>
                </>
            ) : null}

            <span className={"SettingsHeader-spacer"} />

            <div className={"SettingsHeader-search" + (searchOpen && query ? " open" : "")} ref={searchRef}>
                <i className={"fas fa-search"} aria-hidden={true} />
                <input
                    ref={inputRef}
                    type={"search"}
                    value={query}
                    placeholder={t("search_settings")}
                    aria-label={t("search_settings")}
                    data-testid={"settings-search"}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setSearchOpen(true);
                    }}
                    onFocus={() => setSearchOpen(true)}
                    onKeyDown={onSearchKeyDown}
                />
                {query ? (
                    <button
                        type={"button"}
                        className={"SettingsHeader-clear"}
                        aria-label={t("clear")}
                        onClick={() => {
                            setQuery("");
                            inputRef.current?.focus();
                        }}>
                        <i className={"fas fa-times"} aria-hidden={true} />
                    </button>
                ) : (
                    <span className={"SettingsHeader-kbd"}>⌘K</span>
                )}

                {searchOpen && query.trim() ? (
                    <div className={"SettingsMenu results"} role={"listbox"} data-testid={"settings-search-results"}>
                        {results.length === 0 ? (
                            <div className={"SettingsMenu-empty"}>
                                <OwLabel title={"no_settings_match"} noBottomMargin={true} />
                            </div>
                        ) : (
                            results.map((result, index) => {
                                const groupLabel = `${result.sectionTitle} › ${result.cardTitle}`;
                                const showGroup = groupLabel !== lastGroup;
                                lastGroup = groupLabel;

                                return (
                                    <React.Fragment key={result.row.id}>
                                        {showGroup ? <div className={"SettingsMenu-eyebrow"}>{groupLabel}</div> : null}
                                        <button
                                            type={"button"}
                                            role={"option"}
                                            aria-selected={index === cursor}
                                            className={"SettingsMenu-item result" + (index === cursor ? " active" : "")}
                                            data-testid={`search-result-${result.row.id}`}
                                            onMouseEnter={() => setCursor(index)}
                                            onClick={() => pickResult(index)}>
                                            <span className={"SettingsMenu-resultTitle"}>{result.title}</span>
                                            {result.detail ? <span className={"SettingsMenu-resultDetail"}>{result.detail}</span> : null}
                                        </button>
                                    </React.Fragment>
                                );
                            })
                        )}
                        <div className={"SettingsMenu-footer"}>
                            <span>
                                <kbd>↑↓</kbd> <OwLabel title={"move"} noBottomMargin={true} /> · <kbd>↵</kbd>{" "}
                                <OwLabel title={"jump_to_row"} noBottomMargin={true} /> · <kbd>esc</kbd>{" "}
                                <OwLabel title={"close"} noBottomMargin={true} />
                            </span>
                        </div>
                    </div>
                ) : null}
            </div>

            {hasChanges ? (
                <div className={"SettingsHeader-unsaved"} ref={unsavedRef}>
                    <button
                        type={"button"}
                        className={"SettingsChip brand large" + (unsavedOpen ? " open" : "")}
                        aria-haspopup={"dialog"}
                        aria-expanded={unsavedOpen}
                        data-testid={"unsaved-count"}
                        onClick={() => setUnsavedOpen(!unsavedOpen)}>
                        <span className={"SettingsChip-dot"} />
                        {editedCount} <OwLabel title={"unsaved"} noBottomMargin={true} />
                        <i className={"fas " + (unsavedOpen ? "fa-chevron-up" : "fa-chevron-down")} aria-hidden={true} />
                    </button>
                    {unsavedOpen ? (
                        <div className={"SettingsMenu unsaved"} role={"dialog"} aria-label={t("unsaved_changes")}>
                            <div className={"SettingsMenu-head"}>
                                <b>
                                    {editedCount} <OwLabel title={"unsaved_changes"} noBottomMargin={true} />
                                </b>
                            </div>
                            {editedItems.map((item) => (
                                <div key={item.key} className={"SettingsMenu-change"} data-testid={`unsaved-${item.key}`}>
                                    <div className={"SettingsMenu-changeText"}>
                                        <div className={"SettingsMenu-resultTitle"}>{item.title ?? t(item.titleKey)}</div>
                                        <div className={"SettingsMenu-resultDetail"}>
                                            {_.compact([t(item.sectionTitleKey), t(item.cardTitleKey)]).join(" › ")}
                                        </div>
                                    </div>
                                    {!_.isNil(item.before) || !_.isNil(item.after) ? (
                                        <span className={"SettingsMenu-changeValue"}>
                                            <span>{item.before}</span>
                                            <i className={"fas fa-arrow-right"} aria-hidden={true} />
                                            <b>{item.after}</b>
                                        </span>
                                    ) : null}
                                    <button
                                        type={"button"}
                                        className={"SettingsMenu-jump"}
                                        aria-label={t("jump_to_row")}
                                        onClick={() => {
                                            page.jumpToRow(item.rowId);
                                            setUnsavedOpen(false);
                                        }}>
                                        <i className={"fas fa-link"} aria-hidden={true} />
                                    </button>
                                </div>
                            ))}
                            <div className={"SettingsMenu-actions"}>
                                <OwButton
                                    title={"discard_all"}
                                    className={"btn-outline-secondary btn-sm"}
                                    fn={() => {
                                        discardAll();
                                        setUnsavedOpen(false);
                                    }}
                                />
                                <OwButton
                                    title={"save_changes"}
                                    className={"btn-primary btn-sm"}
                                    active={props.saving}
                                    disableOnActive={true}
                                    fn={() => {
                                        saveAll();
                                        setUnsavedOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : (
                <span className={"SettingsHeader-saved"} data-testid={"all-saved"}>
                    <i className={"fas fa-check-circle"} aria-hidden={true} />
                    <OwLabel title={"all_changes_saved"} noBottomMargin={true} />
                </span>
            )}

            <OwButton
                title={"discard"}
                leftIconClass={"fas fa-undo"}
                className={"btn-outline-secondary"}
                disabled={!hasChanges || props.saving}
                fn={discardAll}
                testId={"discard-changes"}
            />
            <OwButton
                title={"save_changes"}
                className={"btn-primary"}
                disabled={!hasChanges}
                active={props.saving}
                disableOnActive={true}
                fn={saveAll}
                testId={"save-changes"}
            />
        </div>
    );
};

export default SettingsHeader;
